import { Box, Chip, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import { SectionWithHeading } from '../../../../shared/components/common/sectionWithHeading';
import { TextWrapper } from '../../../../shared/components/formUtilities/useTextWrapper';
import { DirectDebitService } from '../../../../services/directDebitService';
import { DateHelper } from '../../../../helpers/dateHelper';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { SignWithUaePassButton } from '../../../../shared/components/signWithUaePassButton';
import { DirectDebitDdaStatus, DirectDebitPaymentStatus } from '../../../../constants/directDebitEnums';
import { mapDdaStatusFromApiCode, mapPaymentStatusFromApiCode, getDdaStatusColor } from '../../../../helpers/directDebitStatusHelpers';

export const DirectDebitStatusCard = ({ loanId }) => {
	const directDebitService = new DirectDebitService();
	const { t } = useTranslation();
	const [mandate, setMandate] = useState(null);
	const [loading, setLoading] = useState(false);
	const [nextPayment, setNextPayment] = useState(null);

	useEffect(() => {
		if (loanId) {
			loadMandate();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanId]);

	const loadMandate = () => {
		setLoading(true);
		directDebitService
			.getMandateByLoanId(loanId)
			.then((resp) => {
				setMandate(resp.data);
				if (resp.data) {
					loadNextPayment(resp.data.id);
				}
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const loadNextPayment = (mandateId) => {
		directDebitService
			.getPaymentsByMandateId(mandateId)
			.then((resp) => {
				const payments = resp.data || [];
				const upcoming = payments.find(p => {
					const status = mapPaymentStatusFromApiCode(p.status);
					return status === DirectDebitPaymentStatus.Pending || status === DirectDebitPaymentStatus.UnderProcess;
				});
				setNextPayment(upcoming);
			})
			.catch((err) => {
				console.error('Failed to load payments', err);
			});
	};

	const getStatusIcon = () => {
		if (!mandate) return <CancelOutlinedIcon className="!text-gray-400" />;
		
		const status = mapDdaStatusFromApiCode(mandate.status);
		switch (status) {
			case DirectDebitDdaStatus.Active:
				return <CheckCircleOutlineIcon className="!text-green-500" />;
			case DirectDebitDdaStatus.Pending:
			case DirectDebitDdaStatus.PendingBankSubmission:
			case DirectDebitDdaStatus.PendingBankApproval:
				return <PendingOutlinedIcon className="!text-orange-500" />;
			default:
				return <CancelOutlinedIcon className="!text-red-500" />;
		}
	};

	const getStatusColor = () => {
		if (!mandate) return 'default';
		return getDdaStatusColor(mandate.status);
	};

	return (
		<SectionWithHeading
			heading={t('directDebitStatus')}
			// actionButtons={mandate && mapDdaStatusFromApiCode(mandate.status) === DirectDebitDdaStatus.Pending && (
			// 	<SignWithUaePassButton mandateId={mandate.id} size="medium" />
			// )}
			loading={loading}
			children={
				<Box className="flex flex-col flex-auto justify-between md:!min-h-[200px]">
					<Box className="flex flex-col flex-0">
						<Box className="flex justify-between items-center mb-3 w-full">
							{getStatusIcon()}
							{mandate ? (
								<Chip 
									label={mandate.statusDescription || mandate.status}
									color={getStatusColor()}
									size="small"
								/>
							) : (
								<Chip 
									label={t('noMandate')}
									color="default"
									size="small"
								/>
							)}
						</Box>
						
						{mandate && mapDdaStatusFromApiCode(mandate.status) === DirectDebitDdaStatus.Active && (
							<>
								<Box className="my-3">
									<Divider />
								</Box>
								<Box className="flex justify-between items-center">
									<TextWrapper
										text={t('nextAutoDebit')}
										classList="!font-bold !text-sm"
									/>
									<TextWrapper
										text={nextPayment ? DateHelper.formatDate(nextPayment.paymentDate) : t('noScheduled') || 'N/A'}
										classList="!caption !text-sm"
									/>
								</Box>
								<Box className="my-3">
									<Divider />
								</Box>
								<Box className="flex justify-between items-center">
									<TextWrapper
										text={t('amount') || 'Amount'}
										classList="!font-bold !text-sm"
									/>
									<TextWrapper
										text={nextPayment ? `AED ${nextPayment.amount.toFixed(2)}` : 'N/A'}
										classList="!caption !text-sm"
									/>
								</Box>
								<Box className="my-3">
									<Divider />
								</Box>
								<Box className="flex justify-between items-center">
									<TextWrapper
										text={t('frequency')}
										classList="!font-bold !text-sm"
									/>
									<TextWrapper
										text={mandate.paymentFrequency}
										classList="!caption !text-sm"
									/>
								</Box>
							</>
						)}

						{mandate && mapDdaStatusFromApiCode(mandate.status) !== DirectDebitDdaStatus.Active && (
							<>
								<Box className="my-3">
									<Divider />
								</Box>
								<Box className="text-center">
									<TextWrapper
										text={mapDdaStatusFromApiCode(mandate.status) === DirectDebitDdaStatus.Pending 
											? (t('pendingAuthorization'))
											: (mandate.statusDescription || 'Mandate not active')}
										classList="!text-sm !text-gray-600"
									/>
									
								</Box>
							</>
						)}

						{!mandate && (
							<>
								<Box className="my-3">
									<Divider />
								</Box>
								<Box className="text-center">
									<TextWrapper
										text={t('noDirectDebit') || 'No direct debit mandate set up for this loan'}
										classList="!text-sm !text-gray-600"
									/>
								</Box>
							</>
						)}
					</Box>
				</Box>
			}
		/>
	);
};
